import { useMemo, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SelectField, type FieldDensity, type SelectOption } from '@csa/ui';
import type { TargetKind } from '@csa/shared';
import { api } from '../lib/api.js';

/**
 * Choosing where a request goes.
 *
 * Targets are ordered with production last and the environment is repeated
 * in every option label. Two databases in different environments routinely
 * have the same name, and picking the wrong one is the most consequential
 * mistake available in this tool -- a statement intended for test landing
 * on prod. Making the environment impossible to miss is worth more than a
 * tidy dropdown.
 */

export interface TargetView {
  readonly id: string;
  readonly key: string;
  readonly name: string;
  readonly description: string;
  /**
   * Typed as the closed union rather than `string`, so the console can ask
   * `sqlDialectForKind` what it parses as instead of re-deciding that for
   * itself. A `string` here is what let a second, more lenient copy of that
   * mapping grow in the editor.
   */
  readonly kind: TargetKind;
  readonly environment: string;
  readonly capabilities: {
    readonly read: boolean;
    readonly write: boolean;
    readonly procedures: boolean;
    readonly browseSchema: boolean;
    readonly apiSend: boolean;
    readonly apiMutate: boolean;
  };
  readonly limits: {
    readonly statementTimeoutMs: number;
    readonly interactiveRowCap: number;
    readonly batchRowCap: number;
    readonly maxConcurrency: number;
  };
  readonly allowScripts: boolean;
  readonly procAllowlist: readonly string[];
  readonly schemaAllowlist: readonly string[];
  readonly rest: {
    readonly baseUrl: string;
    readonly pathAllowlist: readonly string[];
    readonly allowedMethods: readonly string[];
    readonly rateLimitPerSecond: number;
  } | null;
  /**
   * Present only on a server-scoped target, and its presence is what the
   * console uses to decide whether a statement has to name a database.
   *
   * A boolean flag would do the same job, but the allowlist is also what the
   * console shows when it explains why a database is missing from the tree,
   * and two fields that have to agree is one more than necessary.
   */
  readonly server: {
    readonly databaseAllowlist: readonly string[];
  } | null;
}

/** Whether a statement against this target has to say which database. */
export function isServerScoped(target: TargetView | null): boolean {
  return target?.server !== null && target?.server !== undefined;
}

/**
 * Production sorts last, not first.
 *
 * A keyboard user tabbing into an unset dropdown lands on the first option,
 * and the first option should never be the one that costs the most to get
 * wrong.
 */
const ENVIRONMENT_ORDER: Record<string, number> = {
  dev: 0,
  test: 1,
  staging: 2,
  prod: 3,
};

function environmentRank(environment: string): number {
  return ENVIRONMENT_ORDER[environment] ?? 9;
}

export function useTargets(kind: 'sql' | 'rest'): {
  targets: TargetView[];
  isPending: boolean;
} {
  const query = useQuery({
    queryKey: ['targets'],
    // Short, because disabling a target is an administrative action taken
    // for a reason, and a console offering a target that was closed off
    // five minutes ago produces a confusing refusal.
    staleTime: 30_000,
    queryFn: () => api.get<{ targets: TargetView[] }>('/api/targets'),
  });

  const targets = useMemo(() => {
    const all = query.data?.targets ?? [];
    const filtered =
      kind === 'rest'
        ? all.filter(
            (t) => t.kind === 'rest' && (t.capabilities.apiSend || t.capabilities.apiMutate),
          )
        : all.filter((t) => t.kind !== 'rest' && t.capabilities.read);

    return [...filtered].sort(
      (a, b) =>
        environmentRank(a.environment) - environmentRank(b.environment) ||
        a.name.localeCompare(b.name),
    );
  }, [query.data, kind]);

  return { targets, isPending: query.isPending };
}

export function TargetPicker({
  targets,
  value,
  onChange,
  label = 'Target',
  disabled = false,
  density = 'default',
}: {
  targets: readonly TargetView[];
  value: string | null;
  onChange: (id: string) => void;
  label?: string;
  disabled?: boolean;
  /** `compact` in the console headers, where this sits in a single dense row. */
  density?: FieldDensity;
}): ReactElement {
  const options = useMemo<SelectOption[]>(
    () =>
      targets.map((target) => ({
        value: target.id,
        // The environment is in the label rather than only in an optgroup,
        // because a collapsed select shows the selected option alone and
        // the group heading is then invisible.
        label: `${target.name} · ${target.environment}`,
      })),
    [targets],
  );

  return (
    <SelectField
      label={label}
      density={density}
      options={options}
      value={value ?? ''}
      disabled={disabled}
      containerClassName={density === 'compact' ? 'min-w-52' : 'min-w-64'}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}
